import React, { useEffect, useRef, useState } from "react";
import LottieReact, { type LottieRefCurrentProps, type LottieComponentProps } from "lottie-react";
import { AspectRatio, type AspectRatioProps } from "@chakra-ui/react";
import { useInView } from "framer-motion";

export type LottieProps = {
  /**
   * The aspect ratio for the animation, which will be used to properly reserve space
   * in the DOM before the animation has loaded.
   *
   * If you need to find the correct ratio, the width and height should be available
   * in the animation JSON file under `w` and `h` top-level properties.
   */
  ratio: AspectRatioProps["ratio"];

  containerProps?: Omit<AspectRatioProps, "ratio">;

  /**
   * Determines when the animation should trigger, based on how much of the element is in the viewport
   * Avoid using `1`, as it can have issues in Safari. Prefer `0.9` or `0.95` instead.
   *
   * @default "some"
   */
  viewportAmount?: "some" | number;

  /**
   * Set a minimum amount of time (in seconds) that must pass before the animation can start playing.
   * Useful when the Lottie is used in combination with a longer entrance animation.
   *
   * @default 0
   */
  minDelay?: number;
} & Pick<LottieComponentProps, "animationData" | "rendererSettings" | "loop" | "initialSegment">;

/**
 * Lottie animation component.
 * Only renders the animation when the component is in view to help with performance.
 * Please check the Lottie animation in Safari; if it lags, use `LottieWithStaticSafariImage` instead.
 */
export function Lottie({
  ratio,
  rendererSettings,
  animationData,
  initialSegment,
  loop,
  viewportAmount = "some",
  minDelay = 0,
  containerProps,
}: LottieProps) {
  const scope = useRef<HTMLDivElement>(null);
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const isInView = useInView(scope, { once: true, amount: viewportAmount });
  const [minDelayHasElapsed, setMinDelayHasElapsed] = useState(false);

  const mergedSettings: LottieComponentProps["rendererSettings"] = {
    progressiveLoad: true,
    // Required to support box shadows: https://github.com/airbnb/lottie-web/wiki/Renderer-Settings#filtersize-svg-renderer
    filterSize: {
      width: "200%",
      height: "200%",
      x: "-50%",
      y: "-50%",
    },
    ...rendererSettings,
  };

  useEffect(() => {
    setTimeout(() => {
      setMinDelayHasElapsed(true);
    }, minDelay * 1000); // Convert to ms
  }, [minDelay]);

  useEffect(() => {
    if (isInView && minDelayHasElapsed) {
      lottieRef.current?.play();
    } else {
      lottieRef.current?.pause();
    }
  }, [isInView, minDelayHasElapsed]);

  return (
    <AspectRatio ref={scope} ratio={ratio} w="100%" {...containerProps}>
      <LottieReact
        autoPlay={false}
        lottieRef={lottieRef}
        loop={loop}
        rendererSettings={mergedSettings}
        animationData={animationData}
        initialSegment={initialSegment}
      />
    </AspectRatio>
  );
}
